import { ErrorHandler } from '@src/components/ErrorHandler';
import { ListTerritoryProvinces } from '@src/includes/functions';
import { fieldValidator } from '@src/includes/validation';
import { isRequired } from '@src/routes/private/MemberProfile/components/Modals/includes/functions';
import { ToolTipStyles } from '@src/routes/private/MemberProfile/components/Modals/includes/styles';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Text } from '@uhg-abyss/web/ui/Text';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';

import fields from '../../../../includes/fields.json';

/**
 * Tab: UsAddress
 *
 * Displays information about a member's policy, relevant to the United States postal address.
 *
 * @param props
 * @returns {Element}
 * @constructor
 */
export function UsAddress(props) {
  const { form, isInternational, isLineValid, targetRef, voidFormDisabled } = props;

  const agencyCaseId = form.watch('ui[usAddress][agencyCaseId]');
  const agentOfRecord = form.watch('ui[usAddress][agentOfRecord]');
  const agentPhoneNumber = form.watch('ui[usAddress][agentPhoneNumber]');
  const agentPhoneType = form.watch('ui[usAddress][agentPhoneType]');
  const city = form.watch('ui[usAddress][city]');
  const countryCode = form.watch('ui[usAddress][countryCode]');
  const guardianFirstName = form.watch('ui[usAddress][guardianFirstName]');
  const guardianLastName = form.watch('ui[usAddress][guardianLastName]');
  const guardianPhoneNumber = form.watch('ui[usAddress][guardianPhoneNumber]');
  const guardianPhoneType = form.watch('ui[usAddress][guardianPhoneType]');
  const postalCode = form.watch('ui[usAddress][postalCode]');
  const stateOrRegion = form.watch('ui[usAddress][stateOrRegion]');
  const streetAddress = form.watch('ui[usAddress][streetAddress]');
  const suite = form.watch('ui[usAddress][suite]');

  /**
   * states
   *
   * Retrieves a list of states.
   *
   * @type {{code: *, name: *}[]}
   */
  const states = useMemo(() => {
    return ListTerritoryProvinces(countryCode);
  }, [countryCode]);

  useEffect(() => {
    if (!isEmpty(agencyCaseId)) {
      form.validate(
        'ui[usAddress][agencyCaseId]',
        () => { },
        () => { }
      );
    }

    if (!isEmpty(agentOfRecord)) {
      form.validate(
        'ui[usAddress][agentOfRecord]',
        () => { },
        () => { }
      );
    }

    if (!isEmpty(guardianFirstName)) {
      form.validate(
        'ui[usAddress][guardianFirstName]',
        () => { },
        () => { }
      );
    }

    if (!isEmpty(guardianLastName)) {
      form.validate(
        'ui[usAddress][guardianLastName]',
        () => { },
        () => { }
      );
    }

    if (!isEmpty(streetAddress)) {
      form.validate(
        'ui[usAddress][streetAddress]',
        () => { },
        () => { }
      );
    }

    if (!isEmpty(suite)) {
      form.validate(
        'ui[usAddress][suite]',
        () => { },
        () => { }
      );
    }

    if (!isEmpty(postalCode)) {
      form.validate(
        'ui[usAddress][postalCode]',
        () => { },
        () => { }
      );
    }
  }, [agentOfRecord, agencyCaseId, guardianFirstName, guardianLastName, streetAddress, suite, postalCode]);

  return (
    <ErrorHandler location="src/routes/private/MemberProfile/components/Modals/Qmcso/components/Address/components/UsAddress/UsAddress.jsx">
      {!isInternational && (
        <Grid className="section first">
          <Box className="box">
            <Heading offset={5}>
              Address Line 1: <span className="required-field" />
            </Heading>
          </Box>
          <span ref={targetRef} />
          <Grid.Col span={{ xs: 12 }}>
            <Text color={isLineValid ? '' : '$error1'} size="$sm">
              <em>Please enter either Agent of Record or Guardian information.</em>
            </Text>
          </Grid.Col>
          <Grid.Col span={{ lg: 7, xl: 7, xs: 12 }}>
            <Grid>
              <Grid.Col span={{ lg: 6, xl: 6, xs: 12 }}>
                <TextInput
                  {...fields?.usAddress?.agentOfRecord}
                  isDisabled={voidFormDisabled}
                  label={
                    <ToolTipStyles>
                      <Tooltip asChild content="Mail will be addressed with ATTN to this Agent of Record">
                        <strong>{fields?.usAddress?.agentOfRecord?.label}</strong>
                      </Tooltip>
                    </ToolTipStyles>
                  }
                  validators={{
                    validate: {
                      customValidator: (value) => {
                        return fieldValidator(fields?.usAddress?.agentOfRecord, value);
                      },
                    },
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ lg: 6, xl: 6, xs: 12 }}>
                <TextInput
                  {...fields?.usAddress?.agencyCaseId}
                  isDisabled={voidFormDisabled}
                  label={
                    <ToolTipStyles>
                      <Tooltip
                        asChild
                        content="The case Identifier is required when communication is being sent to the Agent of Record"
                      >
                        <strong>{fields?.usAddress?.agencyCaseId?.label}</strong>
                      </Tooltip>
                    </ToolTipStyles>
                  }
                  validators={{
                    validate: {
                      customValidator: (value) => {
                        return fieldValidator(fields?.usAddress?.agencyCaseId, value);
                      },
                    },
                  }}
                />
              </Grid.Col>
            </Grid>
          </Grid.Col>
          <Grid.Col span={{ lg: 5, xl: 5, xs: 12 }}>
            <Grid>
              <Grid.Col span="45%">
                <SelectInput
                  {...fields?.usAddress?.agentPhoneType}
                  isDisabled={voidFormDisabled}
                  label={
                    <ToolTipStyles>
                      <Tooltip asChild content="Indicates what type of phone number is being entered for communication">
                        <strong>{fields?.usAddress?.agentPhoneType?.label}</strong>
                      </Tooltip>
                    </ToolTipStyles>
                  }
                  validators={isRequired(agentPhoneNumber)}
                />
              </Grid.Col>
              <Grid.Col span="55%">
                <TextInput
                  {...fields?.usAddress?.agentPhoneNumber}
                  isDisabled={voidFormDisabled || isEmpty(agentPhoneType)}
                />
              </Grid.Col>
            </Grid>
          </Grid.Col>
          <Grid.Col span={{ lg: 7, xl: 7, xs: 12 }}>
            <Grid>
              <Grid.Col span={{ lg: 6, xl: 6, xs: 12 }}>
                <TextInput
                  {...fields?.usAddress?.guardianFirstName}
                  isDisabled={voidFormDisabled}
                  label={
                    <ToolTipStyles>
                      <Tooltip
                        asChild
                        content="Mail will be addressed with ATTN to this person only if there is no Agent of Record indicated. (Agent of record will take precedence)"
                      >
                        <strong>{fields?.usAddress?.guardianFirstName?.label}</strong>
                      </Tooltip>
                    </ToolTipStyles>
                  }
                  validators={{
                    ...isRequired(guardianLastName),
                    ...{
                      validate: {
                        customValidator: (value) => {
                          return fieldValidator(fields?.usAddress?.guardianFirstName, value);
                        },
                      },
                    },
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ lg: 6, xl: 6, xs: 12 }}>
                <TextInput
                  {...fields?.usAddress?.guardianLastName}
                  isDisabled={voidFormDisabled}
                  validators={{
                    ...isRequired(guardianFirstName),
                    ...{
                      validate: {
                        customValidator: (value) => {
                          return fieldValidator(fields?.usAddress?.guardianLastName, value);
                        },
                      },
                    },
                  }}
                />
              </Grid.Col>
            </Grid>
          </Grid.Col>
          <Grid.Col span={{ lg: 5, xl: 5, xs: 12 }}>
            <Grid>
              <Grid.Col span="45%">
                <SelectInput
                  {...fields?.usAddress?.guardianPhoneType}
                  isDisabled={voidFormDisabled}
                  label={
                    <ToolTipStyles>
                      <Tooltip asChild content="Indicates what type of phone number is being entered for communication">
                        <strong>{fields?.usAddress?.guardianPhoneType?.label}</strong>
                      </Tooltip>
                    </ToolTipStyles>
                  }
                  validators={isRequired(guardianPhoneNumber)}
                />
              </Grid.Col>
              <Grid.Col span="55%">
                <TextInput
                  {...fields?.usAddress?.guardianPhoneNumber}
                  isDisabled={voidFormDisabled || isEmpty(guardianPhoneType)}
                />
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>
      )}

      <Grid className={`section ${isInternational ? 'first' : ''}`}>
        <Box className="box">
          <Heading offset={5}>Address Line {isInternational ? 1 : 2}:</Heading>
        </Box>
        <Grid.Col span={{ lg: 4, xl: 4, xs: 12 }}>
          <TextInput
            {...fields?.usAddress?.streetAddress}
            isDisabled={isInternational || voidFormDisabled}
            label={
              <ToolTipStyles>
                <Tooltip
                  asChild
                  content="If address is unknown, enter QMCSO default address until correct address is known"
                >
                  <strong>{fields?.usAddress?.streetAddress?.label}</strong>
                </Tooltip>
              </ToolTipStyles>
            }
            validators={{
              ...fields?.usAddress?.streetAddress?.validators,
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fields?.usAddress?.streetAddress, value);
                },
              },
            }}
          />
        </Grid.Col>
      </Grid>
      <Grid className="section">
        <Box className="box">
          <Heading offset={5}>Address Line {isInternational ? 2 : 3}:</Heading>
        </Box>
        <Grid.Col span={{ lg: 4, xl: 4, xs: 12 }}>
          <TextInput
            {...fields?.usAddress?.suite}
            isDisabled={isInternational || voidFormDisabled}
            validators={{
              ...fields?.usAddress?.suite?.validators,
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fields?.usAddress?.suite, value);
                },
              },
            }}
          />
        </Grid.Col>
      </Grid>
      <Grid className="section">
        <Box className="box">
          <Heading offset={5}>Address Line {isInternational ? 3 : 4}:</Heading>
        </Box>
        <Grid.Col span={{ lg: 3, xl: 3, xs: 12 }}>
          <SelectInput
            {...fields?.usAddress?.stateOrRegion}
            isDisabled={isInternational || voidFormDisabled}
            options={states.map((state) => {
              return {
                label: state?.name,
                value: state?.code,
              };
            })}
          />
        </Grid.Col>
        <Grid.Col span={{ lg: 3, xl: 3, xs: 12 }}>
          <TextInput
            {...fields?.usAddress?.city}
            isDisabled={isInternational || voidFormDisabled || isEmpty(stateOrRegion)}
          />
        </Grid.Col>
        <Grid.Col span={{ lg: 3, xl: 3, xs: 12 }}>
          <TextInput
            {...fields?.usAddress?.postalCode}
            formatter={(value) => {
              return value.replace(/[-`&/\\#,+()$~%.'":*?<>{}@!^_=;\][|]/g, '');
            }}
            isDisabled={isInternational || voidFormDisabled || isEmpty(stateOrRegion) || isEmpty(city)}
            label={
              <ToolTipStyles>
                <Tooltip asChild content="will allow zip + 4 or international zip code">
                  <strong>{fields?.usAddress?.postalCode?.label}</strong>
                </Tooltip>
              </ToolTipStyles>
            }
            validators={{
              ...fields?.usAddress?.postalCode?.validators,
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fields?.usAddress?.postalCode, value);
                },
              },
            }}
          />
        </Grid.Col>
      </Grid>
    </ErrorHandler>
  );
}

UsAddress.propTypes = {
  form: PropTypes.shape({
    getValues: PropTypes.func,
    setValue: PropTypes.func,
  }),
  isAddressValid: PropTypes.bool,
  targetRef: PropTypes.shape({
    current: PropTypes.shape({
      scrollIntoView: PropTypes.func,
    }),
  }),
  TerritoriesProvinces: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  voidFormDisabled: PropTypes.bool,
};

UsAddress.defaultProps = {
  form: {},
  isAddressValid: false,
  targetRef: {},
  TerritoriesProvinces: [],
  voidFormDisabled: false,
};
